// Copyright (c) Ping. All rights reserved.
//
// DragDropOverlay — web-only drop-target affordance. Renders an
// absolute-fill overlay over the chat panel while the user drags files
// in. Pure visual: the host wires the actual DOM `dragenter` /
// `dragover` / `dragleave` / `drop` listeners (web only) and flips
// `visible` / `invalid`; this component never touches the DOM.
//
//   - valid   → blue (accent.indigo) tint + dashed border + upload icon
//               + "Drop to attach"
//   - invalid → red (accent.rose) tint + "Unsupported file type"
//
// Returns null when `!visible` or on native (`document` undefined) —
// drag-and-drop is a desktop/web-only interaction.

import React from 'react';
import { Text, View, type ViewStyle } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { FileWarning, UploadCloud } from 'lucide-react-native';
import { tokens } from '../../design-system/tokens';

export interface DragDropOverlayProps {
  /** Host-controlled — true while files are being dragged over the panel. */
  readonly visible: boolean;
  /** When true, the dragged payload is an unsupported type. */
  readonly invalid?: boolean | undefined;
  readonly testID?: string | undefined;
}

// Convert a hex accent (e.g. `#5C7BF0`) into an rgba tint so the
// overlay washes the panel without fully hiding it. Falls back to the
// raw color if it isn't a 6-digit hex.
function tint(hex: string, alpha: number): string {
  const match = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (match === null || match[1] === undefined) return hex;
  const int = Number.parseInt(match[1], 16);
  const r = (int >> 16) & 0xff;
  const g = (int >> 8) & 0xff;
  const b = int & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const DragDropOverlay = React.memo<DragDropOverlayProps>(
  ({ visible, invalid, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];

    // Native has no drag-and-drop; bail before any DOM assumptions.
    if (typeof document === 'undefined') return null;
    if (!visible) return null;

    const isInvalid = invalid === true;
    const accent = isInvalid ? colors.accent.rose : colors.accent.indigo;
    const Icon = isInvalid ? FileWarning : UploadCloud;
    const label = isInvalid ? 'Unsupported file type' : 'Drop to attach';

    const overlayStyle: ViewStyle = {
      backgroundColor: tint(accent, 0.12),
      borderColor: accent,
      // Inner inset so the dashed border reads as a drop-zone frame
      // rather than hugging the panel edge.
      borderWidth: 2,
    };

    return (
      // Absolute-fill drop-zone affordance. `border-dashed` +
      // tinted fill are className-first; the theme-dynamic accent
      // color is the documented inline-style fallback.
      <View
        className="absolute inset-0 z-50 m-sm rounded-lg border-2 border-dashed items-center justify-center"
        style={overlayStyle}
        pointerEvents="none"
        accessibilityRole="alert"
        accessibilityLabel={label}
        testID={testID}
      >
        <View className="items-center justify-center gap-sm">
          <Icon size={40} strokeWidth={2} color={accent} />
          <Text
            className="font-sans text-[16px] leading-6 font-semibold text-center"
            style={{ color: accent }}
          >
            {label}
          </Text>
        </View>
      </View>
    );
  },
);

DragDropOverlay.displayName = 'DragDropOverlay';
